import { apiClient } from './apiClient';
import { browserLocation } from './geolocation';

export interface ConversationSource {
  label: string;
  href?: string;
  verified?: boolean;
}

export interface ConversationToolMetadata {
  name: string;
  status: string;
  detail?: string;
}

export type WeatherConditionCode = 'clear' | 'partly_cloudy' | 'cloudy' | 'rain' | 'storm' | 'snow' | 'fog' | 'wind' | 'unknown';

export interface WeatherHourData {
  time: string;
  conditionCode: WeatherConditionCode;
  temperatureC: number;
  precipitationPercent: number;
  windKph: number;
}

export interface WeatherDayData {
  date: string;
  dayLabel: string;
  highC: number;
  lowC: number;
  precipitationPercent: number;
  conditionCode: WeatherConditionCode;
}

export interface WeatherCardData {
  kind: 'weather';
  location: string;
  dateLabel: string;
  condition: string;
  conditionCode: WeatherConditionCode;
  temperatureC: number;
  feelsLikeC: number;
  highC: number;
  lowC: number;
  precipitationPercent: number;
  windKph: number;
  windDirection: string;
  humidityPercent: number | null;
  summary: string;
  hourly: WeatherHourData[];
  // Optional 5-day forecast -- absent on conversation history persisted
  // before this field existed (see lib/liveData.js's sanitizeWeatherCard()).
  daily?: WeatherDayData[];
}

export interface PlaceCardData {
  kind: 'place';
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  mapsUrl: string;
  websiteUrl: string;
  type: string;
  summary: string;
  // Same-origin server proxy paths only (e.g. "/maptile?..", "/placephoto?..") --
  // see lib/liveData.js's sanitizeMapTilePath()/sanitizePlacePhotoPath().
  mapTileUrl?: string;
  // Only present when Google actually has a real photo on file for the
  // place (e.g. a landmark) -- never fabricated when absent.
  photoUrl?: string;
}

export type DirectionsTravelMode = 'DRIVE' | 'WALK' | 'BICYCLE' | 'TRANSIT';

export interface DirectionsCardData {
  kind: 'directions';
  origin: string;
  destination: string;
  mode: DirectionsTravelMode;
  distanceMeters: number;
  distanceText: string;
  durationSeconds: number;
  durationText: string;
  mapsUrl: string;
  summary: string;
}

// A real OZIRA Code workspace project built and applied server-side by
// lib/chatservice.js's processWebsiteBuild -- `html` is already fully
// inlined (lib/staticPreview.js), so it renders standalone in an iframe
// with no further fetches. `files` lists the real saved file paths.
export interface WebBuildCardData {
  kind: 'webbuild';
  projectId: string;
  projectName: string;
  html: string;
  files: string[];
  // Public, openable address for the built site (server.js's /site/<token>),
  // so the page can be opened in a real browser tab and shared, not only
  // viewed in the inline frame. Absent on builds made before this existed.
  url?: string;
}

// Thin typed wrappers over the REAL, already-shipping conversation/project
// endpoints in ozira-ai's server.js (same ones ozira-mobile's src/api.js
// already calls) plus the three narrow additions made in this phase
// (rename via /conversations/meta, ?q= search, /conversations/export).
// No new persistence model was invented -- see Gate 1/2 discovery notes.

export interface ConversationSummary {
  id: string;
  title: string;
  source?: string;
  count: number;
  folderId: string | null;
  projectId: string | null;
  pinned: boolean;
  archived: boolean;
}

export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  image?: string;
  model?: string;
  reaction?: 1 | -1 | 0;
  // Real per-message cost from /api/chat's response (lib/chatservice.js) --
  // undefined for messages sent before this field existed, never fabricated.
  credits?: number;
  metadata?: {
    sources?: ConversationSource[];
    verified?: boolean;
    tool?: ConversationToolMetadata;
    weather?: WeatherCardData;
    place?: PlaceCardData;
    places?: PlaceCardData[];
    directions?: DirectionsCardData;
    webBuild?: WebBuildCardData;
  };
}

export interface ConversationDetail {
  id: string;
  userId: string;
  title: string;
  source?: string;
  projectId?: string | null;
  folderId?: string | null;
  pinned?: boolean;
  archived?: boolean;
  titleLocked?: boolean;
  createdAt: number;
  updatedAt?: number;
  messages: ConversationMessage[];
}

export interface ProjectSummary {
  id: string;
  name: string;
  description: string;
  color: string;
  status: string;
  role: string;
  counts: { members: number; chats: number; items: number; tasksOpen: number; decisions: number; credits: number };
}

export async function listConversations(params: { folder?: string; project?: string; q?: string } = {}): Promise<ConversationSummary[]> {
  const search = new URLSearchParams();
  if (params.q && params.q.trim()) {
    search.set('q', params.q.trim());
  } else {
    if (params.folder) search.set('folder', params.folder);
    if (params.project) search.set('project', params.project);
  }
  const qs = search.toString();
  const { data } = await apiClient.call<{ conversations: ConversationSummary[] }>(`/api/conversations${qs ? `?${qs}` : ''}`);
  return data.conversations;
}

export async function getConversation(id: string): Promise<ConversationDetail> {
  const { data } = await apiClient.call<{ conversation: ConversationDetail }>('/api/conversations/get', { body: { id } });
  return data.conversation;
}

export async function saveConversation(payload: {
  id?: string;
  title?: string;
  projectId?: string | null;
  messages: ConversationMessage[];
}): Promise<{ id: string; title: string }> {
  const { data } = await apiClient.call<{ id: string; title: string }>('/api/conversations/save', { body: payload });
  return data;
}

export async function deleteConversation(id: string): Promise<boolean> {
  const { data } = await apiClient.call<{ deleted: boolean }>('/api/conversations/delete', { body: { id } });
  return data.deleted;
}

export interface ConversationMetaPatch {
  pinned?: boolean;
  archived?: boolean;
  folderId?: string | null;
  projectId?: string | null;
  title?: string;
}

export async function setConversationMeta(id: string, patch: ConversationMetaPatch) {
  const { data } = await apiClient.call<{
    ok: boolean;
    title: string;
    folderId: string | null;
    projectId: string | null;
    pinned: boolean;
    archived: boolean;
  }>('/api/conversations/meta', { body: { id, ...patch } });
  return data;
}

export async function exportConversation(id: string) {
  const { data } = await apiClient.call<{ exportedAt: string; conversation: ConversationDetail }>('/api/conversations/export', {
    body: { id },
  });
  return data;
}

export async function listProjects(includeArchived = false): Promise<ProjectSummary[]> {
  const { data } = await apiClient.call<{ projects: ProjectSummary[] }>(`/api/projects${includeArchived ? '?archived=1' : ''}`);
  return data.projects;
}

export async function createProject(name: string): Promise<ProjectSummary> {
  const { data } = await apiClient.call<{ project: ProjectSummary }>('/api/projects/create', { body: { name } });
  return data.project;
}

export interface ChatResult {
  reply: string;
  model?: string;
  modelLabel?: string;
  images?: Array<{ id: string; url: string; mediaType?: string }>;
  metadata?: ConversationMessage['metadata'];
  credits?: number;
}

export async function sendChat(params: {
  messages: { role: string; content: string; metadata?: ConversationMessage['metadata'] }[];
  projectId?: string | null;
  lang?: string;
  toolMode?: string;
  model?: string;
  effort?: string;
  // Explicit image-generation intent, bypassing server.js's wording-based
  // isImageGenerationRequest() guess -- set when the user picked "Create
  // image" from the tools menu rather than typing an action+visual phrase.
  skill?: string;
  // One of lib/providers.js's IMAGE_SIZES ('1024x1024' | '1024x1536' |
  // '1536x1024'). Only meaningful together with skill: 'image'.
  size?: string;
  // Editing an existing, owned generated image (server.js's /api/chat image
  // branch forwards both straight into processImage(), the same contract
  // POST /api/image/edit uses) -- set together with skill: 'image' from the
  // image viewer's Edit/Redesign/Reframe/Remove-background actions.
  sourceUrl?: string;
  operation?: string;
}): Promise<ChatResult> {
  // 'auto' is the same default every other OZIRA surface (mobile, Telegram,
  // scheduled tasks) sends -- the backend picks the best-fit model per task.
  // Matches the Figma "Auto model" pill; POST /api/chat 400s with
  // "Unknown model" if this is omitted. A caller (ModelSelector) can
  // override it with a real config.models id.
  // A provider fallback cycle can outlive the shared API client's short
  // default used by ordinary reads. The gateway bounds each provider attempt
  // and this window lets the browser receive its fallback response.
  // browserLocation() only ever prompts once per session and resolves fast
  // from cache afterward (see geolocation.ts) -- awaiting it here is what
  // lets every sendChat() caller automatically include the user's location
  // without any of them needing to ask for it themselves.
  const coords = await browserLocation();
  const { data } = await apiClient.call<ChatResult>('/api/chat', {
    body: {
      model: 'auto',
      timezone: browserTimeZone(),
      ...(coords ? { latitude: coords.latitude, longitude: coords.longitude } : {}),
      ...params,
    },
    timeoutMs: 45000,
  });
  return data;
}

// The browser already knows its own current timezone for free
// (Intl.DateTimeFormat().resolvedOptions().timeZone) -- sent with every chat
// request so the backend can answer "what time is it" deterministically,
// with no geolocation permission prompt and no paid lookup on either side.
// Every sendChat() caller gets this automatically; none needs to pass it.
function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

// POST /api/ai/vision (server.js -> lib/ai-ext.js visionComplete) -- the
// composer's "Upload file" tool. Images only (PNG/JPEG/WebP/non-animated
// GIF, server-enforced); there is no generic document-analysis backend, so
// this is genuinely what "upload a file" does today, not a stand-in for it.
export async function sendVisionMessage(params: {
  messages: { role: string; content: string }[];
  images: Array<{ base64: string; mimeType: string }>;
  lang?: string;
  effort?: string;
}): Promise<{ reply: string; modelLabel?: string; provider?: string; creditCost?: number }> {
  const { data } = await apiClient.call<{ reply: string; modelLabel?: string; provider?: string; creditCost?: number }>('/api/ai/vision', {
    body: params,
    timeoutMs: 45000,
  });
  return data;
}

export interface ResearchSource {
  url: string;
  title?: string;
}

export interface ResearchResult {
  configured: boolean;
  text: string;
  sources?: ResearchSource[];
  notice?: string;
  error?: string;
}

// POST /api/ai/research (server.js -> lib/ai-ext.js research(), Gemini +
// Google Search grounding) -- the "+" menu's Web search / Deep research
// items were setting client-only state (toolMode) that server.js's /api/chat
// handler never reads (grepped -- zero matches), so picking either one had
// no effect on the actual reply. This is the one real backend both should
// call; there is no server-side "deep" vs "shallow" distinction today
// (research()'s only non-default intent is 'weather'), so both tools are
// currently the same underlying capability under different labels -- not
// fabricating a depth difference that doesn't exist server-side.
export async function sendResearch(query: string): Promise<ResearchResult> {
  const { data } = await apiClient.call<ResearchResult>('/api/ai/research', {
    body: { query },
    timeoutMs: 45000,
  });
  return data;
}

// GET /api/plans (server.js) -- the same real model/effort catalogue every
// OZIRA surface (Telegram, dev API) already renders its own pickers from.
export interface PlanModel {
  id: string;
  label: string;
  provider: string;
  tier: string;
  creditCost: number;
}

export interface PlanEffort {
  id: string;
  labelEn: string;
  labelAm: string;
  creditMultiplier: number;
}

// A subscription tier (Free/Basic/Pro/Premium), distinct from PlanModel
// (an AI model choice) -- same GET /api/plans response, different field.
export interface SubscriptionPlan {
  id: string;
  nameEn: string;
  priceETB: number;
  priceUSD: number;
  creditsPerMonth: number;
  tier: string;
}

export interface PlansCatalogue {
  models: PlanModel[];
  efforts: PlanEffort[];
  plans: SubscriptionPlan[];
}

export async function getPlans(): Promise<PlansCatalogue> {
  const { data } = await apiClient.call<{ models: PlanModel[]; efforts: PlanEffort[]; plans: SubscriptionPlan[] }>('/api/plans');
  return { models: data.models || [], efforts: data.efforts || [], plans: data.plans || [] };
}

export interface SubscribeResult {
  activated?: boolean;
  checkoutUrl?: string;
}

export async function subscribeToPlan(planId: string): Promise<SubscribeResult> {
  const { data } = await apiClient.call<SubscribeResult>('/api/subscribe', { body: { planId } });
  return data;
}

export interface AccountProfile {
  birthday?: string;
}

export async function getProfile(): Promise<AccountProfile> {
  const { data } = await apiClient.call<{ profile?: AccountProfile }>('/api/profile');
  return data.profile || {};
}

// GET /api/storage (server.js) -- real per-category byte usage.
export interface StorageUsage {
  imageBytes: number;
  codeBytes: number;
  designBytes: number;
  fileBytes: number;
  totalBytes: number;
  capBytes: number;
}

export async function getStorageUsage(): Promise<StorageUsage> {
  const { data } = await apiClient.call<StorageUsage>('/api/storage');
  return data;
}

// GET /api/me (server.js) -- the account's real name, plan, and usage. Used
// by the sidebar's credits/plan footer (WebApp.dc.html) instead of the
// Principal-minting call SessionContext already makes for auth, which
// deliberately keeps only user.id (spec section 16).
export interface AccountSubscription {
  planId: string;
  planName: string;
  tier: string;
  expiresAt: number | null;
  used: number;
  quota: number;
}

export interface AccountInfo {
  name: string;
  subscription: AccountSubscription | null;
}

export async function getMe(): Promise<AccountInfo> {
  const { data } = await apiClient.call<{ user: { name?: string }; subscription: AccountSubscription | null }>('/api/me');
  return { name: data.user?.name || '', subscription: data.subscription || null };
}

export interface DesignProjectSummary {
  id: string;
  name: string;
  updatedAt: number;
}

// GET /api/design/projects (lib/designworkspace.js#listProjects) -- real,
// already-persisted Design projects. Informational only: the embedded
// Design engine (public/design-app) has no URL parameter to deep-link into
// a specific project, so this list is never rendered as clickable.
export async function getDesignProjects(): Promise<DesignProjectSummary[]> {
  const { data } = await apiClient.call<{ projects: DesignProjectSummary[] }>('/api/design/projects');
  return data.projects || [];
}

export interface SpeakResult {
  configured: boolean;
  // Playable directly as an <audio>/Audio() src -- lib/ai-ext.js's speak()
  // always returns a data: URI, never a remote URL.
  audio?: string;
  engine?: string;
  error?: string;
  notice?: string;
}

export async function speak(params: { text: string; voice?: string; lang?: string }): Promise<SpeakResult> {
  const { data } = await apiClient.call<SpeakResult>('/api/ai/speak', { body: params });
  return data;
}

export interface ShareGrant {
  shareGrantId: string;
  token: string;
  expiresAt: number | null;
  auditCorrelationId: string;
}

export async function createShare(params: {
  resourceType: 'conversation' | 'response';
  resourceId: string;
  messageIndex?: number;
  // Matches lib/shareGrants.js's createShare() accepted option names exactly.
  includeFiles?: boolean;
  includeSources?: boolean;
  expiresAt?: number;
}): Promise<ShareGrant> {
  const { data } = await apiClient.call<ShareGrant>('/api/shares/create', { body: params });
  return data;
}

export async function revokeShare(shareGrantId: string): Promise<{ revoked: boolean }> {
  const { data } = await apiClient.call<{ revoked: boolean }>('/api/shares/revoke', { body: { shareGrantId } });
  return data;
}

export interface ResolvedShareMessage {
  role: 'user' | 'assistant';
  content: string;
  image?: string;
}

export type ResolvedShare =
  | { resourceType: 'conversation'; title: string; messages: ResolvedShareMessage[] }
  | { resourceType: 'response'; title: string; message: ResolvedShareMessage; sources?: ConversationSource[] };

// GET /api/shares/resolve is the one deliberately UNAUTHENTICATED read in
// this app (lib/shareGrants.js's resolveShare()) -- a share link must work
// for whoever holds the token, signed in or not.
export async function resolveShare(token: string): Promise<ResolvedShare> {
  const { data } = await apiClient.call<ResolvedShare>(`/api/shares/resolve?token=${encodeURIComponent(token)}`);
  return data;
}

// Response Save = bookmark ONLY, never OZIRA memory (Gate B, Conversation
// Workspace V2 closure). A reference (conversationId + messageIndex), never
// a copy of message content -- see lib/messageBookmarks.js server-side.
export async function saveBookmark(conversationId: string, messageIndex: number): Promise<{ bookmarked: boolean }> {
  const { data } = await apiClient.call<{ bookmarked: boolean }>('/api/bookmarks/save', { body: { conversationId, messageIndex } });
  return data;
}

export async function unsaveBookmark(conversationId: string, messageIndex: number): Promise<{ bookmarked: boolean }> {
  const { data } = await apiClient.call<{ bookmarked: boolean }>('/api/bookmarks/unsave', { body: { conversationId, messageIndex } });
  return data;
}

export async function listBookmarkedIndexes(conversationId: string): Promise<number[]> {
  const { data } = await apiClient.call<{ messageIndexes: number[] }>(`/api/bookmarks/list?conversationId=${encodeURIComponent(conversationId)}`);
  return data.messageIndexes;
}

export type FeedbackValue = 1 | -1;

// Safe-metadata-only by construction: caller must never pass raw conversation
// content here -- only a message id, screen, model route, language, scope,
// and an optional short free-text category (spec: "must never auto-attach
// conversation body").
export async function submitFeedback(params: {
  value: FeedbackValue;
  model?: string;
  lang?: string;
  text?: string;
}) {
  const { data } = await apiClient.call('/api/feedback', { body: params });
  return data;
}
